// Hero section — transparent background so the PageField canvas shows through.
// Content fades in 300ms after mount to avoid flash on first render.

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

function Arrow() {
  return (
    <svg
      width={14} height={14} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17L17 7M17 7H8M17 7V16" />
    </svg>
  );
}

const Hero = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="hero"
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        background: "transparent",
        overflow: "hidden",
        zIndex: 3,
      }}
    >
      {/* Content: centered in lower portion so pyramid occupies upper 39% */}
      <div
        style={{
          position: "absolute",
          left: 0, right: 0, top: "39%", bottom: 0,
          zIndex: 4,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          padding: "0 24px",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.9s ease",
        }}
      >
        <div
          style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", textAlign: "center",
            gap: "26px", maxWidth: "840px", width: "100%",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 300,
              fontSize: "clamp(46px, 5.6vw, 82px)",
              lineHeight: 1.05,
              letterSpacing: "-0.025em",
              color: "#FFFFFF",
            }}
          >
            Ship it. Govern it.{" "}
            <span style={{ color: "#7BB5B1" }}>Trust it.</span>
          </h1>

          <p
            className="hero-subtitle"
            style={{
              margin: 0,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 300,
              fontSize: 19,
              lineHeight: 1.55,
              color: "rgba(225,232,231,0.74)",
              maxWidth: 600,
              letterSpacing: "0.002em",
            }}
          >
            Production-grade systems for operations-heavy startups and
            SMBs — engineered with the oversight built in, not bolted on.
          </p>

          <div
            className="hero-cta-group"
            style={{
              display: "flex", gap: "14px", marginTop: "6px",
              flexWrap: "wrap", justifyContent: "center",
            }}
          >
            <a
              href="/#contact"
              onClick={(e) => {
                const contactEl = document.getElementById("contact");
                if (!contactEl) return; // let the browser navigate to /#contact instead
                e.preventDefault();
                contactEl.scrollIntoView({ behavior: "smooth" });
              }}
              className="hero-btn-primary"
              style={{
                display: "inline-flex", alignItems: "center", gap: 9,
                padding: "15px 26px", borderRadius: "8px",
                border: "1px solid rgba(91,148,144,0.55)",
                background: "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
                color: "#FFFFFF",
                fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 16,
                letterSpacing: "-0.01em", cursor: "pointer",
                boxShadow: "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)",
                transition: "all 0.2s ease",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)";
                e.currentTarget.style.transform  = "translateY(-2px)";
                e.currentTarget.style.boxShadow  = "0 0 32px rgba(91,148,144,0.48), 0 6px 20px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)";
                e.currentTarget.style.transform  = "translateY(0)";
                e.currentTarget.style.boxShadow  = "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)";
              }}
            >
              Book a 30-min AI strategy call <Arrow />
            </a>

            <Link
              to="/portfolio"
              className="hero-btn-secondary"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "15px 26px", borderRadius: "8px",
                border: "1px solid rgba(255,255,255,0.22)",
                background: "linear-gradient(135deg, rgba(91,148,144,0.14) 0%, rgba(45,92,90,0.07) 100%)",
                color: "#F5F7F7",
                fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 16,
                letterSpacing: "-0.01em", cursor: "pointer",
                boxShadow: "0 0 14px rgba(91,148,144,0.16), inset 0 1px 0 rgba(255,255,255,0.08)",
                transition: "all 0.2s ease",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.42)";
                e.currentTarget.style.background  = "linear-gradient(135deg, rgba(91,148,144,0.22) 0%, rgba(45,92,90,0.14) 100%)";
                e.currentTarget.style.transform   = "translateY(-2px)";
                e.currentTarget.style.boxShadow   = "0 0 20px rgba(91,148,144,0.22), inset 0 1px 0 rgba(255,255,255,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)";
                e.currentTarget.style.background  = "linear-gradient(135deg, rgba(91,148,144,0.14) 0%, rgba(45,92,90,0.07) 100%)";
                e.currentTarget.style.transform   = "translateY(0)";
                e.currentTarget.style.boxShadow   = "0 0 14px rgba(91,148,144,0.16), inset 0 1px 0 rgba(255,255,255,0.08)";
              }}
            >
              See case studies
            </Link>
          </div>

          <div
            data-testid="hero-proof"
            style={{
              display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center",
              fontFamily: "Montserrat, sans-serif", fontSize: 13, fontWeight: 400,
              color: "rgba(225,232,231,0.55)", marginTop: 4,
            }}
          >
            <span>★ 5.0 on Google</span>
            <span>Clients in 5 countries</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
